import { Activity, Eye, Download, FileOutput, Edit, Copy, Share2, Upload, Filter } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { FluentCard } from '@/components/layout/page-header'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { getAvatarColor, getInitials } from '@/lib/avatar-colors'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TemplateUsageEvent, TemplateEventType } from '@/lib/api/types'

interface EventsTableProps {
  data: TemplateUsageEvent[]
  loading?: boolean
  showTemplateColumn?: boolean
}

const EVENT_ICONS: Record<TemplateEventType, typeof Eye> = {
  View: Eye,
  Download: Download,
  Generate: FileOutput,
  Edit: Edit,
  Clone: Copy,
  Share: Share2,
  Export: Upload,
}

const EVENT_COLORS: Record<TemplateEventType, string> = {
  View: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Download: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  Generate: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  Edit: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Clone: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  Share: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  Export: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
}

const ALL_EVENT_TYPES: TemplateEventType[] = [
  'View',
  'Download',
  'Generate',
  'Edit',
  'Clone',
  'Share',
  'Export',
]

export function EventsTable({ data, loading, showTemplateColumn = true }: EventsTableProps) {
  const [filterType, setFilterType] = useState<TemplateEventType | 'all'>('all')

  const filteredData =
    filterType === 'all' ? data : data.filter((e) => e.eventType === filterType)

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const formatDuration = (ms?: number) => {
    if (!ms) return null
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  if (loading) {
    return (
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-pink-400 to-rose-600">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Events</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Latest template activity</p>
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 animate-pulse"
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
            </div>
          ))}
        </div>
      </FluentCard>
    )
  }

  return (
    <FluentCard>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-pink-400 to-rose-600">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Events</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Latest template activity</p>
          </div>
        </div>

        {/* Filter */}
        <Select
          value={filterType}
          onValueChange={(v) => setFilterType(v as TemplateEventType | 'all')}
        >
          <SelectTrigger className="w-[140px] h-9">
            <Filter className="h-4 w-4 mr-2 opacity-50" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {ALL_EVENT_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filteredData.length > 0 ? (
        <div className="space-y-2">
          {filteredData.map((event) => {
            const Icon = EVENT_ICONS[event.eventType]
            return (
              <div
                key={event.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {/* User avatar */}
                {(() => {
                  const nameParts = event.userName.split(' ')
                  const initials = getInitials(nameParts[0], nameParts[1])
                  const color = getAvatarColor(event.userName)
                  return (
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarFallback className={`rounded-full text-xs font-medium ${color.bg} ${color.text}`}>
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  )
                })()}

                {/* Event info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                      {event.userName}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                      {event.eventType.toLowerCase()}d
                    </span>
                    {showTemplateColumn && (
                      <span className="font-medium text-gray-700 dark:text-gray-300 text-sm truncate max-w-[200px]">
                        {event.templateName}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>{formatTimestamp(event.timestamp)}</span>
                    {event.departmentName && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <span>{event.departmentName}</span>
                      </>
                    )}
                    {event.durationMs && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <span>{formatDuration(event.durationMs)}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Event type badge */}
                <div
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                    EVENT_COLORS[event.eventType]
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {event.eventType}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <Activity className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No events to display</p>
        </div>
      )}
    </FluentCard>
  )
}
